/**
 * @fileoverview `<DataTable>` — patron de table de données GNPC.
 *
 * Composé sur `@tanstack/react-table` v8 avec le `<Table>` shadcn (bordures,
 * spacing, radius). Fournit d'un seul coup :
 *   - Colonnes typées (via generics `<TData>`) ;
 *   - Tri par en-tête cliquable (`sorting: true`) ;
 *   - Pagination CURSOR opaque via `onNextPage` / `onPreviousPage` — jamais
 *     `page: number`, conformément au guide frontend (§CursorPagination) ;
 *   - `EmptyState` intégré (voir invariant : liste vide ≠ erreur) ;
 *   - `Skeleton` loading state ;
 *   - Slot filtres au-dessus + slot actions à droite.
 *   - Cartes mobiles optionnelles via `mobileCard`, pour éviter les tableaux
 *     compressés sur téléphone et tablette étroite.
 *   - Action de ligne optionnelle (`rowAction`) : ajoute automatiquement une
 *     colonne de fin avec un bouton chevron accessible (desktop) et un bouton
 *     « Ouvrir » en fin de carte (mobile) ; la ligne entière reste cliquable
 *     à la souris en confort.
 *
 * NOTE : ce composant est le **standard de fait** de toutes les tables
 * backoffice (PaymentsList, AuditLog, RefundsQueue, UsersList). Ne pas
 * réinventer une table ailleurs — ajouter les besoins ici via props.
 *
 * @see Documents/Paymetrust_Guide_Integration_Frontend_v1.pdf §CursorPagination
 */

import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { ChevronDown, ChevronRight, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { useState, type MouseEvent, type ReactNode } from 'react';
import { Button } from './ui/button.tsx';
import { Skeleton } from './ui/skeleton.tsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table.tsx';
import { cn } from '../lib/utils.ts';

/**
 * Action principale d'une ligne.
 *
 * Utiliser quand une table aurait autrement une seule colonne bouton
 * ("Détail", "Ouvrir", "Analyser").
 *
 * Accessibilité (validateur W3C : `role` interdit sur `<tr>` ; axe) : le
 * contrôle accessible est un VRAI `<button>` — colonne de fin (chevron) sur
 * desktop, bouton « Ouvrir » en fin de carte mobile — porteur de
 * `aria-label={label(row)}`, activable au clavier et annoncé par les
 * lecteurs d'écran. Le clic sur la ligne / la carte entière est conservé
 * comme simple confort de souris (aucune sémantique ARIA dessus).
 */
export interface DataTableRowAction<TData> {
  /** Libellé accessible, ex. `Ouvrir le paiement 01J…`. */
  label: (row: TData) => string;
  /** Callback appelé quand l'utilisateur active la ligne. */
  onSelect: (row: TData) => void;
}

/**
 * Props publiques. `TData` est le type d'une ligne, propagé aux `columns`
 * pour un typage strict des accessors.
 */
export interface DataTableProps<TData> {
  /** Définition des colonnes — voir `@tanstack/react-table` `ColumnDef`. */
  columns: ColumnDef<TData>[];
  /** Données à afficher. Un tableau vide déclenche l'`emptyState`. */
  data: TData[];
  /**
   * État de chargement — quand `true`, rend `skeletonRows` lignes vides.
   * Utilisé pendant le premier fetch et les paginations.
   */
  loading?: boolean;
  /** Nombre de lignes squelette à rendre pendant `loading`. Défaut : 5. */
  skeletonRows?: number;
  /**
   * Rendu à afficher quand `data.length === 0` et `!loading`. En GNPC,
   * un résultat vide n'est PAS une erreur — passer un `<EmptyState>`
   * au ton positif ou neutre.
   */
  emptyState?: ReactNode;
  /**
   * Zone au-dessus de la table (filtres, recherche, boutons d'action).
   * Aucun contenu par défaut.
   */
  toolbar?: ReactNode;
  /**
   * Rendu carte pour mobile. À fournir pour les listes métier backoffice :
   * chaque carte doit afficher l'identifiant principal, le statut, les montants
   * et les actions essentielles sans nécessiter de scroll horizontal.
   */
  mobileCard?: (row: TData) => ReactNode;
  /**
   * Label accessible de la liste mobile. Défaut : `"Liste"`.
   */
  mobileCardLabel?: string;
  /**
   * Action principale de ligne. À utiliser seulement lorsqu'il existe une
   * action unique pour chaque ligne : le composant rend alors lui-même la
   * colonne bouton de fin (chevron) — ne pas déclarer de colonne "Détail"
   * en plus dans `columns`.
   */
  rowAction?: DataTableRowAction<TData>;
  /**
   * Handlers de pagination cursor opaque. `hasNextPage` et `hasPreviousPage`
   * pilotent l'état actif/désactivé des boutons. `page` et `pageSize` sont
   * indicatifs (affichage "Page X — Y résultats") : la vérité vit côté API.
   */
  pagination?: {
    onNextPage?: () => void;
    onPreviousPage?: () => void;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    /** Texte à afficher entre les boutons — ex : `"20 résultats"`. */
    label?: string;
  };
  /** Classe supplémentaire sur le conteneur racine. */
  className?: string;
}

function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;

  return Boolean(
    target.closest(
      'a, button, input, select, textarea, summary, [role="button"], [data-row-action-ignore]',
    ),
  );
}

/**
 * Confort souris : clic n'importe où sur la ligne / carte, SAUF sur un
 * élément interactif imbriqué (CopyButton, lien, le bouton d'action
 * lui-même — sinon double déclenchement de `onSelect` par bubbling).
 */
function activateRow<TData>(
  event: MouseEvent,
  row: TData,
  rowAction: DataTableRowAction<TData>,
) {
  if (isInteractiveTarget(event.target)) return;
  rowAction.onSelect(row);
}

/**
 * Table de données GNPC.
 *
 * @example
 * ```tsx
 * const columns: ColumnDef<Payment>[] = [
 *   { accessorKey: 'id', header: 'ID' },
 *   { accessorKey: 'amount', header: 'Montant', cell: ({ getValue }) => <Amount value={getValue()} /> },
 * ];
 * <DataTable
 *   columns={columns}
 *   data={payments}
 *   mobileCard={(payment) => <PaymentCard payment={payment} />}
 *   rowAction={{ label: (payment) => `Ouvrir ${payment.id}`, onSelect: openPayment }}
 *   pagination={{ ... }}
 * />
 * ```
 */
export function DataTable<TData>({
  columns,
  data,
  loading = false,
  skeletonRows = 5,
  emptyState,
  toolbar,
  mobileCard,
  mobileCardLabel,
  rowAction,
  pagination,
  className,
}: DataTableProps<TData>): React.JSX.Element {
  const [sorting, setSorting] = useState<SortingState>([]);
  const table = useReactTable({
    data,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const showEmpty = !loading && data.length === 0;
  const showRows = !loading && data.length > 0;
  const hasMobileCards = Boolean(mobileCard);
  const rows = table.getRowModel().rows;

  return (
    <div className={cn('flex w-full min-w-0 flex-col gap-3', className)}>
      {toolbar && (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          {toolbar}
        </div>
      )}

      {hasMobileCards && (
        /*
         * `role="list"` + `role="listitem"` : le validateur W3C rejette un
         * `aria-label` sur un `<div>` sans rôle. On ne pose la sémantique de
         * liste (et son label) QUE lorsque des lignes sont affichées — les
         * squelettes de chargement et l'empty state ne sont pas des items de
         * liste et resteraient invalides comme enfants d'un `role="list"`.
         */
        <div
          className="grid gap-3 md:hidden"
          role={showRows ? 'list' : undefined}
          aria-label={showRows ? (mobileCardLabel ?? 'Liste') : undefined}
        >
          {loading &&
            Array.from({ length: skeletonRows }).map((_, i) => (
              <div key={`mobile-skeleton-${i}`} className="rounded-lg border border-border bg-card p-4">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="mt-3 h-5 w-44" />
                <Skeleton className="mt-3 h-4 w-full" />
                <Skeleton className="mt-2 h-9 w-full" />
              </div>
            ))}

          {showRows &&
            rows.map((row) =>
              rowAction ? (
                /*
                 * La carte peut contenir des contrôles imbriqués (CopyButton…),
                 * donc PAS de `<button>` englobant ni de rôle interactif sur le
                 * div : le clic sur la carte est un confort de souris, et le
                 * bouton « Ouvrir » ci-dessous est le contrôle accessible
                 * (clavier + lecteur d'écran).
                 */
                <div key={row.id} role="listitem">
                  <div
                    onClick={(event) => activateRow(event, row.original, rowAction)}
                    className="cursor-pointer rounded-lg transition-colors [&_article]:transition-colors hover:[&_article]:border-quipux-orange-500/60 hover:[&_article]:bg-quipux-orange-50/20"
                  >
                    {mobileCard?.(row.original)}
                    <div className="mt-2 flex justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={rowAction.label(row.original)}
                        onClick={() => rowAction.onSelect(row.original)}
                      >
                        Ouvrir
                        <ChevronRight className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={row.id} role="listitem">
                  {mobileCard?.(row.original)}
                </div>
              ),
            )}

          {showEmpty && (
            <div className="rounded-lg border border-border bg-card">
              {emptyState ?? (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  Aucun résultat.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div
        className={cn(
          // `min-w-0` : sans lui, la largeur max-content d'une table à
          // nombreuses colonnes remonte jusqu'à la grille de page et étire
          // (puis fait déborder) tout le contenu. Avec `min-w-0`, la table
          // scrolle DANS ce conteneur au lieu d'élargir la page.
          'min-w-0 rounded-lg border border-border',
          hasMobileCards ? 'hidden overflow-x-auto md:block' : 'overflow-x-auto',
        )}
      >
        <Table>
          <TableHeader className="bg-muted/50">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className="hover:bg-transparent">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-foreground"
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {sorted === 'asc' ? (
                            <ChevronUp className="size-3.5" />
                          ) : sorted === 'desc' ? (
                            <ChevronDown className="size-3.5" />
                          ) : (
                            <ChevronsUpDown className="size-3.5 opacity-50" />
                          )}
                        </button>
                      ) : (
                        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                      )}
                    </TableHead>
                  );
                })}
                {rowAction && (
                  /* Colonne de fin dédiée au bouton d'action de ligne :
                   * en-tête masqué visuellement mais annoncé aux lecteurs
                   * d'écran pour que la colonne reste nommée. */
                  <TableHead className="w-10">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                )}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  {columns.map((_col, j) => (
                    <TableCell key={j}>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                  ))}
                  {rowAction && (
                    <TableCell>
                      <Skeleton className="size-8" />
                    </TableCell>
                  )}
                </TableRow>
              ))}

            {showRows &&
              table.getRowModel().rows.map((row) => (
                /*
                 * Pas de `role` / `tabIndex` / `aria-label` sur le `<tr>` :
                 * le validateur W3C interdit `role` sur `tr` et le pattern
                 * « ligne-lien » est un piège lecteur d'écran. Le `onClick`
                 * n'est qu'un confort de souris ; le contrôle accessible est
                 * le bouton chevron de la colonne de fin.
                 */
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? 'selected' : undefined}
                  onClick={
                    rowAction
                      ? (event) => activateRow(event, row.original, rowAction)
                      : undefined
                  }
                  className={cn(rowAction && 'cursor-pointer')}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                  {rowAction && (
                    <TableCell className="py-1.5 text-right">
                      <button
                        type="button"
                        aria-label={rowAction.label(row.original)}
                        onClick={() => rowAction.onSelect(row.original)}
                        className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      >
                        <ChevronRight className="size-4" aria-hidden="true" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}

            {showEmpty && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={columns.length + (rowAction ? 1 : 0)} className="p-0">
                  {emptyState ?? (
                    <p className="py-12 text-center text-sm text-muted-foreground">
                      Aucun résultat.
                    </p>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && (
        <div className="flex flex-col gap-3 pt-1 text-sm sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">{pagination.label ?? ''}</span>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.onPreviousPage}
              disabled={!pagination.hasPreviousPage}
            >
              Précédent
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={pagination.onNextPage}
              disabled={!pagination.hasNextPage}
            >
              Suivant
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
